/*
 * Host configuration.
 *
 * The editor is designed to be embedded in someone else's page, and that page
 * — not the editor — owns the accounts. Everything the host needs to say about
 * itself arrives in one global object that has to be defined BEFORE this bundle
 * loads, because the menu bar reads it while rendering for the first time:
 *
 *   <script>
 *     window.scratchConfig = {
 *       session: {token: '...', username: '张三', avatar: './avatar.png'},
 *       menuBar: {
 *         myStuff: {url: '/myProject'},
 *         userAvatar: {handleClick: () => openMyAccountMenu()}
 *       }
 *     };
 *   </script>
 *   <script src="chunks/gui.js"></script>
 *
 * Reading it once here keeps `window` lookups out of the components and puts
 * every default in a single place.
 */

import {readEnvironment} from './host-bridge.js';

const ACCOUNT_UI_DEFAULT = false;

const isPlainObject = value => (
    typeof value === 'object' && value !== null && !Array.isArray(value)
);

// A host may leave a key out entirely or pass null; both mean "use the default".
const isMissing = value => value === null || typeof value === 'undefined';

// Copy the values the host supplied over the defaults, ignoring anything of the
// wrong shape. A typo in a host page should degrade to a sensible default rather
// than crash the editor before it has drawn anything.
const mergeKnown = (defaults, supplied, path) => {
    const result = {};
    Object.keys(defaults).forEach(key => {
        const fallback = defaults[key];
        const value = isPlainObject(supplied) ? supplied[key] : null;
        if (isPlainObject(fallback)) {
            result[key] = mergeKnown(fallback, value, `${path}.${key}`);
            return;
        }
        if (isMissing(value)) {
            result[key] = fallback;
            return;
        }
        // A host callback is optional everywhere, so it is accepted wherever the
        // default is a function or an explicit null. Rejecting it here would have
        // silently discarded every callback a host registered.
        if (typeof value === 'function' && (typeof fallback === 'function' || fallback === null)) {
            result[key] = value;
            return;
        }
        if (typeof value !== typeof fallback) {
            // eslint-disable-next-line no-console
            console.warn(
                `[openblock] scratchConfig${path}.${key} should be a ${typeof fallback}; ` +
                `using the default instead.`
            );
            result[key] = fallback;
            return;
        }
        result[key] = value;
    });
    return result;
};

const DEFAULTS = {
    /*
     * Identity supplied by the host page. The editor never asks for a password
     * and never creates an account; an empty token simply means "signed out".
     */
    session: {
        token: '',
        username: '',
        avatar: ''
    },
    /*
     * The editor used to ship its own sign-in, sign-up, "my projects" and
     * administration screens. That is the host page's job now, so this defaults
     * to off: embed the editor and no account UI appears at all. A host that
     * genuinely wants the built-in screens can opt back in.
     */
    account: {
        enable: ACCOUNT_UI_DEFAULT
    },
    projectInfo: {
        projectName: '',
        authorUsername: '',
        authorAvatar: ''
    },
    logo: {
        show: true,
        url: '',
        handleClick: null
    },
    menuBar: {
        languageButton: {show: true, defaultLanguage: ''},
        newButton: {show: true, handleBefore: null},
        loadFileButton: {show: true, handleBefore: null},
        saveFileButton: {show: true, handleBefore: null},
        turboModeButton: {show: true},
        helpButton: {show: true, handleBefore: null},
        /*
         * The host's identity entry. The editor owns no account, so clicking
         * this only ever points at the host: either a URL to jump to, or a
         * callback into the host page. The URL is followed in the top window,
         * because the account page belongs to the host and not to this frame.
         */
        userAvatar: {show: true, url: '', handleClick: null},
        /*
         * Where "my projects" lives. The editor stores nothing and lists
         * nothing, so this is either a URL to navigate to or a callback into
         * the host page. A host that configures neither gets no button at all,
         * rather than one that does nothing when clicked.
         */
        myStuff: {show: true, url: '', handleClick: null},
        customButtons: []
    },
    /*
     * The local helper that talks to the board. A page cannot see what is
     * installed on a computer, so there are only two ways to reach it: talk to
     * the loopback port it listens on, or hand the system a URL scheme and let
     * it start the program. Both are configurable because the port and the
     * scheme belong to the helper, not to the editor.
     */
    kidsblockLink: {
        wsUrl: 'ws://127.0.0.1:20111',
        scheme: 'kidsblocklink://',
        downloadUrls: {
            windows: 'https://github.com/kidsblock/kidsblock-releases/releases/download/v2.0.5/kidsblock.Link.2.0.5.Setup.exe',
            mac: 'https://github.com/kidsblock/kidsblock-releases/releases/download/v2.0.5/kidsblock.Link.2.0.5.dmg'
        }
    },
    stageArea: {
        fullscreenButton: {show: true},
        startButton: {show: true},
        stopButton: {show: true}
    },
    blocks: {
        scale: 1,
        hideCatagorys: [],
        hideBlocks: []
    },
    assets: {
        assetHost: '',
        defaultIndex: {
            sprites: '',
            costumes: '',
            backdrops: '',
            sounds: ''
        }
    },
    defaultProjectURL: '',
    handleVmInitialized: null,
    handleProjectLoaded: null,
    handleDefaultProjectLoaded: null
};

let cached = null;

// Custom buttons are free-form, so they are validated by hand rather than by
// the shape-matching above.
const readCustomButtons = supplied => {
    const raw = isPlainObject(supplied) && isPlainObject(supplied.menuBar) ?
        supplied.menuBar.customButtons : null;
    if (!Array.isArray(raw)) return [];
    return raw
        .filter(button => isPlainObject(button) && typeof button.buttonName === 'string')
        .map(button => ({
            buttonName: button.buttonName,
            show: button.show !== false,
            style: isPlainObject(button.style) ? button.style : {},
            handleClick: typeof button.handleClick === 'function' ? button.handleClick : null
        }));
};

// Kept separate so the `window` lookup happens once and reads plainly.
const hostConfig = () => {
    if (typeof window === 'undefined') return null;
    return window.scratchConfig;
};

const readConfig = () => {
    const supplied = hostConfig();
    const config = mergeKnown(DEFAULTS, supplied, '');
    config.menuBar.customButtons = readCustomButtons(supplied);
    /*
     * A host that renders its own page hands the identity over in
     * `window.scratchConfig`. A host that embeds the editor in an iframe cannot
     * write into this document at all, so it passes the same identity on the
     * URL instead (`?embedded=1&username=...`). Both end up here so the menu bar
     * has one thing to read. `readEnvironment` returns null unless `embedded=1`,
     * which is what keeps a standalone editor from showing an account entry it
     * was never given.
     */
    const urlSession = typeof window === 'undefined' ? {} :
        (readEnvironment(window.location.search, document.referrer) || {});
    if (!config.session.username) config.session.username = urlSession.username || '';
    if (!config.session.avatar) config.session.avatar = urlSession.avatar || '';
    // Naming a user does NOT switch the built-in account screens back on: the
    // host supplies a name precisely so the editor stops managing accounts. The
    // flag is the only way in, and it defaults to off.
    return config;
};

const getConfig = () => {
    if (!cached) cached = readConfig();
    return cached;
};

// Only used by tests, which need a clean read between cases.
const resetConfig = () => {
    cached = null;
};

const getSession = () => getConfig().session;

const isSignedIn = () => Boolean(getSession().token || getSession().username);

const isAccountUiEnabled = () => getConfig().account.enable === true;

const getMenuBarConfig = () => getConfig().menuBar;

// A host callback is optional everywhere, and a host callback that throws must
// not take the editor down with it.
const runHostHandler = (handler, fallback) => (...args) => {
    if (typeof handler === 'function') {
        try {
            return handler(...args);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[openblock] a scratchConfig callback threw:', error);
            return null;
        }
    }
    return typeof fallback === 'function' ? fallback(...args) : null;
};

export {
    getConfig,
    getMenuBarConfig,
    getSession,
    isAccountUiEnabled,
    isSignedIn,
    resetConfig,
    runHostHandler
};
